import {
  lesson_plan_path as lessonPlanPath,
  wiki_path as wikiPath
} from 'routes/routes'
import location from 'utils/location'
import debug from 'debug'

const log = debug('app:clients/curriculum_widget_navigate_handler')

const HIERARCHY_DEPENDENCIES = {
  unit: 'grade',
  section: 'unit',
  lesson: 'section',
}

function isPresent(value) {
  return value !== undefined && value !== null && value !== ''
}

function validatePayload(payload) {
  if (!payload || typeof payload !== 'object') {
    return { valid: false, error: 'Payload must be a non-null object' }
  }

  if (!isPresent(payload.curriculum)) {
    return { valid: false, error: 'Missing required field: curriculum' }
  }

  if (!isPresent(payload.grade)) {
    return { valid: false, error: 'Missing required field: grade' }
  }

  for (const [field, dependency] of Object.entries(HIERARCHY_DEPENDENCIES)) {
    if (isPresent(payload[field]) && !isPresent(payload[dependency])) {
      return { valid: false, error: `Cannot specify ${field} without ${dependency}` }
    }
  }

  if (isPresent(payload.lesson) && typeof payload.lesson !== 'number') {
    return { valid: false, error: 'lesson must be a number' }
  }

  return { valid: true, error: null }
}

function determineNavigationTarget(payload) {
  if (isPresent(payload.lesson)) return 'lesson'
  if (isPresent(payload.section)) return 'section'
  if (isPresent(payload.unit)) return 'unit'
  return 'grade'
}

function navigateToLesson(lessonPlanId) {
  const path = lessonPlanPath(lessonPlanId)
  log('Navigating to lesson:', path)
  location.assign(path)
}

function navigateToWiki(contentId) {
  const path = wikiPath(contentId)
  log('Navigating to wiki:', path)
  location.assign(path)
}

function resolveWikiContentId(payload) {
  const params = new URLSearchParams({
    curriculum: payload.curriculum,
    grade: payload.grade,
  })

  if (isPresent(payload.unit)) params.set('unit', payload.unit)
  if (isPresent(payload.section)) params.set('section', payload.section)

  return fetch(`/api/v1/curriculum_navigate?${params}`, {
    headers: { Accept: 'application/json' },
  }).then((response) => {
    if (!response.ok) {
      return response.json().then((body) => {
        throw new Error(body.error || `Navigation resolution failed: ${response.status}`)
      })
    }
    return response.json()
  })
}

function handleNavigateToEvent(payload) {
  const { valid, error } = validatePayload(payload)
  if (!valid) {
    log('Invalid navigateTo payload:', error, payload)
    return Promise.reject(new Error(error))
  }

  const target = determineNavigationTarget(payload)
  log('Processing navigateTo event:', { target, payload })

  if (target === 'lesson') {
    navigateToLesson(payload.lesson)
    return Promise.resolve()
  }

  return resolveWikiContentId(payload).then((result) => {
    navigateToWiki(result.content_id)
  }).catch((err) => {
    log('Navigation failed:', err.message)
    throw err
  })
}

export { validatePayload, determineNavigationTarget, handleNavigateToEvent, isPresent }
export default handleNavigateToEvent
